import React, { useMemo, useContext } from 'react';
import { cloneDeep } from 'lodash';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  MODAL_KEY, PRODUCT_INFO,
  INTAKE, PATIENT, PRESCRIBER,
  KIT_EMAIL, PRESCRIPTION, APPROVAL_VALIDATION_FIELD as AVF,
  APPROVAL_VALIDATION_ERROR,
  SF_EMAIL_TEMPLATES, RESPONSE_POPUP_KEY,
  KIT, Eversesnse, Axena, PRODUCT_TYPE, ACTION_TYPE,
  DOSE_SPOT, Dexcom, Apretude, Vyepti, VISIT_URL,
  JUMP_START_INFO,
} from '../../../constants/patientChart.constant';
import { POPUP_TYPE } from '../../subComponents/popup';
import { pickEditorState } from '../../subComponents/editor/jodit/v2';
import DoseSpotUI, { DoseSpotActions } from '../DoseSpotUI';
import RxUI, { RxActions } from '../RxUI';
import useCmnTemplate from '../../../hooks/useCmnTemplate';
import { ORDER_AND_PRESCRIBER_FLAGS, SAVED_FLAGS } from '../../../constants/visits.constant';
import { FEATURE } from '../../../utils/feature';

const hasFeature = FEATURE();

const validateEmail = ({
  kitKey = '',
  editorStates = new Map(),
  email: _email = KIT_EMAIL(),
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  const email = { ..._email };
  const editorState = pickEditorState({
    name: kitKey,
    state: editorStates,
  })
  if (hasFeature.RICH_TEXT_EDITOR) {
    email.emailMessage = editorState?.text;
  }

  // clear all possible errors first
  validationError.delete(`${kitKey}-${AVF.subjectLine}`);
  validationError.delete(`${kitKey}-${AVF.emailMessage}`);

  // validate the errors
  if (email?.subjectLine?.length < 3) {
    validationError.set(`${kitKey}-${AVF.subjectLine}`, { field: AVF.subjectLine, message: '' });
  }

  if (email?.emailMessage?.length < 3) {
    validationError.set(`${kitKey}-${AVF.emailMessage}`, { field: AVF.emailMessage, message: '' });
  }
};

const validatePrescription = ({
  kitKey = '',
  prescription = PRESCRIPTION(),
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${kitKey}-${AVF.DaysSupply}`);
  validationError.delete(`${kitKey}-${AVF.Refills}`);
  validationError.delete(`${kitKey}-${AVF.Note}`);
  validationError.delete(`${kitKey}-${AVF.Sig}`);

  // validate the errors
  if (Number(prescription?.DaysSupply) === 0) {
    validationError.set(`${kitKey}-${AVF.DaysSupply}`, { field: AVF.DaysSupply, message: '' });
  }

  if (prescription?.Refills?.DefaultOption === -1) {
    validationError.set(`${kitKey}-${AVF.Refills}`, { field: AVF.Refills, message: '' });
  }

  if (prescription?.Note?.length < 3) {
    validationError.set(`${kitKey}-${AVF.Note}`, { field: AVF.Note, message: '' });
  }

  if (prescription?.Sig?.length < 3) {
    validationError.set(`${kitKey}-${AVF.Sig}`, { field: AVF.Sig, message: '' });
  }
};

const validatePin = ({
  pin = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.pin}`);

  if (pin.length !== 4) {
    validationError.set(`${AVF.pin}`, { field: AVF.pin, message: '' });
  }
};

const validateEversenseTemplate = ({
  formField = {},
  template = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.eversenseTemplate}`);

  /**
   * @description Eversense template validation starts here
   */
  const requiredFormFieldsInEversense = [
    {
      name: 'icd10Diagnosis',
      value: formField.icd10Diagnosis.trim(),
    },
    {
      name: 'checkbox-hhu',
      value: formField['checkbox-hhu'],
    },
    {
      name: 'checkbox-icgm',
      value: formField['checkbox-icgm'],
    },
    {
      name: 'checkbox-lticgm',
      value: formField['checkbox-lticgm'],
    },
    {
      name: 'checkbox-faitr',
      value: formField['checkbox-faitr'],
    },
    {
      name: 'checkbox-moti',
      value: formField['checkbox-moti'],
    }
  ];
  const emptyFieldFoundInEversense = requiredFormFieldsInEversense.find((field) => field.value === '');
  if (emptyFieldFoundInEversense) {
    validationError.set(`${AVF.eversenseTemplate}`, { field: AVF.eversenseTemplate, message: '' });
  }
};

const validateAxenaTemplate = ({
  formField = {},
  template = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.axenaTemplate}`);

  /**
   * @description Axena template validation starts here
   */
  const requiredFormFieldsInAxena = [
    {
      name: 'otherICD10Code',
      value: formField.otherICD10Code.trim(),
    }
  ];
  const emptyFieldFoundInAxena = requiredFormFieldsInAxena.find((field) => field.value === '');
  if (emptyFieldFoundInAxena) {
    validationError.set(`${AVF.axenaTemplate}`, { field: AVF.axenaTemplate, message: '' });
  }
};

const validateDexcomTemplate = ({
  formField = {},
  template = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.dexcomTemplate}`);

  /**
   * @description Dexcom template validation starts here
   */
  const requiredFormFieldsInDexcom = [
    {
      name: 'otherICD10Code',
      value: formField.otherICD10Code.trim()
    },
  ];
  const emptyFieldFound = requiredFormFieldsInDexcom.find((field) => field.value === '');
  if (emptyFieldFound) {
    validationError.set(`${AVF.dexcomTemplate}`, { field: AVF.dexcomTemplate, message: '' });
  }
};

const validateApretudeTemplate = ({
  formField = {},
  template = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.apretudeTemplate}`);

  /**
   * @description Apretude template validation starts here
   */
  const requiredFormFieldsInApretude = [
    {
      name: 'icd10Diagnosis',
      value: formField.icd10Diagnosis.trim()
    },
  ];
  const emptyFieldFound = requiredFormFieldsInApretude.find((field) => field.value === '');
  if (emptyFieldFound) {
    validationError.set(`${AVF.apretudeTemplate}`, { field: AVF.apretudeTemplate, message: '' });
  }
};

const validateVyeptiTemplate = ({
  formField = {},
  template = '',
  validationError = APPROVAL_VALIDATION_ERROR(),
}) => {
  // clear all possible errors first
  validationError.delete(`${AVF.apretudeTemplate}`);

  /**
   * @description Apretude template validation starts here
   */
  const requiredFormFieldsInVyepti = [
    {
      name: 'icd10Diagnosis',
      value: formField.icd10Diagnosis.trim(),
    },
  ];
  const emptyFieldFound = requiredFormFieldsInVyepti.find((field) => field.value === '');
  if (emptyFieldFound) {
    validationError.set(`${AVF.apretudeTemplate}`, { field: AVF.apretudeTemplate, message: '' });
  }
};

/**
 * 
 * @param {{
 *  modifyDoseSpotPrescription: (props: {
 *    OrderId: string;
 *    DoctorId: string;
 *  }) => Promise<{}>;
 * }} param0 
 * @returns {React.JSX.Element}
 */
export const Approve = ({
  orderId = 0,
  patientId = 0,
  doctorId = 0,
  product = PRODUCT_INFO(),
  patient = PATIENT(),
  history = {},
  prescriber = PRESCRIBER(),
  intake = INTAKE(),
  medicalOrderNotes = {},
  cmnTemplateFormField = {},
  savedFlags = SAVED_FLAGS(),
  flags = ORDER_AND_PRESCRIBER_FLAGS(),
  approveEmailTemplatesV2: templates = SF_EMAIL_TEMPLATES(),
  doseSpot = DOSE_SPOT(),

  getCmnPdfData = async (requestBody = {}) => {},
  setCmnTemplateFormField = () => {},
  getScheduledVisits = async (requestBody = {}) => {},
  getAsyncOrders = async (requestBody = {}) => {},
  removeOrdersFromLV = (requestBody = {}) => {},
  verifyPin = async (requestBody = {}) => {},
  getDoseSpot = async (requestBody = {}) => {},
  resetDoseSpot = async () => {},
  approveOrder = async (requestBody = {}, callback = () => {}, routePath = '', called = true) => {},
  getEmails = async () => {},
  modifyDoseSpotPrescription,
  handler = { route: () => {} },
}) => {
  const {
    kit,
    setKit,
    pin,
    loading,
    startLoading,
    stopLoading,
    setJumpStart,
    visitType,
    isConfiguredAlternateRx,
    joditEditorTemplateForKit,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const { template: tN, preparePDF, fileName, imageQuality, pageOptions } = useCmnTemplate({ product, patient });

  const kits = useMemo(() => {
    return [...kit.entries()].map(([key, item]) => ({
      ...item,
      key,
    }));
  }, [kit]);

  const validationError = useMemo(() => {
    const validationError = APPROVAL_VALIDATION_ERROR();
    const formField = cloneDeep(cmnTemplateFormField);

    const TemplateValidator = new Map([
      [Eversesnse, {
        validate: validateEversenseTemplate,
        data: { template: tN, formField, validationError },
      }],
      [Axena, {
        validate: validateAxenaTemplate,
        data: { template: tN, formField, validationError },
      }],
      [Dexcom, {
        validate: validateDexcomTemplate,
        data: { template: tN, formField, validationError },
      }],
      [Apretude, {
        validate: validateApretudeTemplate,
        data: { template: tN, formField, validationError },
      }],
      [Vyepti, {
        validate: validateVyeptiTemplate,
        data: { template: tN, formField, validationError },
      }],
    ]);

    if (TemplateValidator.has(tN)) {
      const { validate, data } = TemplateValidator.get(tN);
      validate(data);
    }

    kits.forEach(({ key: kitKey, email, items }) => {
      validateEmail({
        kitKey,
        email,
        editorStates: joditEditorTemplateForKit,
        validationError,
      });
      items.forEach((prescription) => validatePrescription({ kitKey, prescription, validationError }));
    });

    validatePin({ pin, validationError });

    return validationError;
  }, [cmnTemplateFormField, joditEditorTemplateForKit, kits, pin]);

  const { productType, actionType, hasAlternateRx, rxPID, doseSpotURL } = useMemo(() => {
    const defaultType = {
      hasAlternateRx: false,
      rxPID: 0,
      doseSpotURL: '',
      productType: PRODUCT_TYPE.regularRx,
      actionType: ACTION_TYPE.regularRx,
    };

    const { StringValue, openCommonModal } = doseSpot;

    const items = kits.flatMap(({ items }) => items);

    for (let i = 0; i < items?.length; i++) {
      const item = items[i];

      const isAlternateRx = isConfiguredAlternateRx({
        pId: item.ProductId,
        isAlternateRx: Boolean(product?.IsAlternateRx),
        skipCheckingChangeProducts: true,
        skipCheckingAppSettings: true,
      });

      if (isAlternateRx && openCommonModal) {
        return {
          hasAlternateRx: true,
          rxPID: item.ProductId,
          doseSpotURL: StringValue,
          productType: PRODUCT_TYPE.alternateRx,
          actionType: ACTION_TYPE.alternateRx,
        };
      }

      if (isAlternateRx) {
        return {
          hasAlternateRx: true,
          rxPID: item.ProductId,
          doseSpotURL: StringValue,
          productType: PRODUCT_TYPE.regularRx,
          actionType: ACTION_TYPE.regularRx,
        };
      }
    }

    return defaultType;
  }, [kits, doseSpot, isConfiguredAlternateRx]);

  const TemplateCollection = new Map([
    [PRODUCT_TYPE.alternateRx, <DoseSpotUI url={doseSpotURL} productId={rxPID} orderId={orderId} />],
    [PRODUCT_TYPE.regularRx, (
      <RxUI
        orderId={orderId}
        patientId={patientId}
        doctorId={doctorId}
        product={product}
        patient={patient}
        history={history}
        prescriber={prescriber}
        intake={intake}
        kits={kits}
        medicalOrderNotes={medicalOrderNotes}
        formField={cmnTemplateFormField}
        templates={templates}
        validationError={validationError}

        getCmnPdfData={getCmnPdfData}
        setFormField={setCmnTemplateFormField}
      />
    )]
  ]);

  const executeApprove = async ({
    key,
    _loading = new Set(loading),
  }) => {
    const requestBody = {
      OrderId: orderId,
      PatientId: patientId,
      DoctorId: doctorId,
      PIN: Number(pin),
      OrderFlags: [],
      PrescriberFlags: [],
      PrescriptionData: [],
      EmailTemplates: [],
      EmailSubject: '',
      EmailMessage: '',
    };

    const etsCollection = new Map([
      [0, {
        ProductId: 0,
        TemplateId: 0,
        Template: '',
        Subject: '',
        Message: '',
      }],
    ]);
    etsCollection.delete(0);

    try {
      for (let i = 0; i < kits?.length; i++) {
        const { key, email: _email, items, kitPId } = kits[i];
        const { editorState } = joditEditorTemplateForKit.get(key) || { editorState: { text: '', value: '' } };
        const email = { ..._email };

        if (hasFeature.RICH_TEXT_EDITOR) {
          email.emailMessage = editorState.value;
        }

        if (kitPId > 0) {
          requestBody.EmailSubject = email.subjectLine;
          requestBody.EmailMessage = email.emailMessage;

          etsCollection.set(kitPId, {
            ProductId: kitPId,
            TemplateId: 0,
            Template: '',
            Subject: email.subjectLine,
            Message: email.emailMessage,
          });
        }
  
        for (let j = 0; j < items?.length; j++) {
          const item = items[j];
          const prescription = {
            ProductId: item.ProductId,
            PharmacyId: item.PharmacyId,
            DaysSupply: Number(item.DaysSupply),
            QTY: item.Qty,
            TotalPills: item.TotalPills,
            Refills: item.Refills.DefaultOption,
            Sig: item.Sig,
            SigUpdated: !item.IsAlternateRx,
            NotePrefix: item.NotePrefix,
            Note: item.Note,
            PrescriptionNote: item.Note,
            ProductIdForKit: item.ProductIdForKit,
          };

          if (kitPId === 0) {
            const et = {
              ProductId: item.ProductId,
              TemplateId: 0, // email.value,
              Template: email.templateTitle,
              Subject: email.subjectLine,
              Message: email.emailMessage,
            };
            prescription.Email = et;
            etsCollection.set(et.ProductId, et);
          }

          requestBody.PrescriptionData.push(prescription);
        }
      }
  
      for (let i = 0; i < flags?.api?.data?.OrderFlagsV2?.length; i++) {
        const flag = flags.api.data.OrderFlagsV2[i];
        const flagResponse = savedFlags.flagsCollection.get(`0-${flag.flagId}`) || { note: '' };
        requestBody.OrderFlags.push({
          FlagText: flag.flagText,
          FlagResponse: flagResponse.note,
        });
      }
  
      for (let i = 0; i < flags?.api?.data?.PrescriberFlags?.length; i++) {
        const flag = flags.api.data.PrescriberFlags[i];
        const flagResponse = savedFlags.flagsCollection.get(`${flag.ProductId}-${flag.QuestionId}`) || { note: '' };
        requestBody.PrescriberFlags.push({
          FlaggedQuestion: flag.FlaggedQuestion,
          FlaggedAnswer: flag.FlaggedAnswer,
          PrescriberFlagResponse: flagResponse.note,
        });
      }

      if (tN) {
        requestBody.cmn_base64_pdf = await preparePDF(
          fileName,
          [pageSelector, pageWithMaxPossibleWidthSelector],
          pageOptions,
          pageElementSelector,
          imageQuality,
          generatePDF
        );
      }

      requestBody.EmailTemplates = [...etsCollection.values()];
    } catch(e) {
      stopLoading(_loading, MODAL_KEY.APPROVE);

      handleModalOpen({
        key: RESPONSE_POPUP_KEY.APPROVE,
        modal: {
          heading: 'Error',
          type: POPUP_TYPE.ERROR,
          message: 'Failed to prepare request body. Please try later.',
        }
      });

      return {};
    }

    try {
      const { status, data } = await approveOrder(requestBody, () => {}, '', true);
      if (status === 200) {
        stopLoading(_loading, MODAL_KEY.APPROVE);
        setKit(KIT());
        removeOrdersFromLV({ orderIds: [orderId] });
        handleMultipleModalClose.call(this, { keys: [key, MODAL_KEY.APPROVE] });
        getScheduledVisits();
        getAsyncOrders();

        // route only when video call in not on going
        if (!_loading.has(MODAL_KEY.TWILIO)) {
          const visitUrl = VISIT_URL.get(visitType);
          handler.route(visitUrl);
        }

        if (_loading.has(MODAL_KEY.TWILIO) && hasFeature.IS_SALESFORCE_CONFIGURED) {
          getEmails({ OrderId: orderId });
        }
      }

      if (status !== 200) {
        const { Error: error = 'Failed to approve the order. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key: RESPONSE_POPUP_KEY.APPROVE,
          modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
      // error handling when needed
    } finally {
      stopLoading(_loading, MODAL_KEY.APPROVE);
    }

    return {};
  };

  const handleApprove = async ({ key }) => {
    const _loading = startLoading(loading, MODAL_KEY.APPROVE);

    try {
      const { data: { Data = '' } } = await verifyPin({ PIN: pin });
      if (Data !== 'Valid PIN') {
        stopLoading(_loading, MODAL_KEY.APPROVE);

        handleModalOpen({
          key: RESPONSE_POPUP_KEY.APPROVE,
          modal: {
            heading: 'Error',
            type: POPUP_TYPE.ERROR,
            message: 'Invalid PIN entry.',
          }
        });

        return {};
      }
    } catch (e) {
      stopLoading(_loading, MODAL_KEY.APPROVE);

      handleModalOpen({
        key: RESPONSE_POPUP_KEY.APPROVE,
        modal: {
          heading: 'Error',
          type: POPUP_TYPE.ERROR,
          message: 'Failed to verify PIN. Please try later.',
        }
      });

      return {};
    }

    if (hasAlternateRx) {
      await getDoseSpot({ OrderId: orderId, ProductId: rxPID });
      stopLoading(_loading, MODAL_KEY.APPROVE);
      return {};
    }

    return await executeApprove({ key, _loading });
  };

  const handleApproveViaDoseSpot = async ({ key }) => {
    const _loading = startLoading(loading, MODAL_KEY.APPROVE);
    return await executeApprove({ key, _loading });
  };

  const handleCancel = async () => {
    setJumpStart(JUMP_START_INFO());
    resetDoseSpot();

    if (actionType === ACTION_TYPE.alternateRx) {
      modifyDoseSpotPrescription({
        OrderId: `${orderId}`,
        DoctorId: `${doctorId}`,
      });
    }
  };

  const ActionCollection = new Map([
    [ACTION_TYPE.alternateRx, (
      <DoseSpotActions
        validationError={validationError}
        onApprove={handleApproveViaDoseSpot}
        onCancel={handleCancel}
      />
    )],
    [ACTION_TYPE.regularRx, (
      <RxActions
        validationError={validationError}
        onApprove={handleApprove}
      />
    )]
  ]);

  return (
    <div className='flex flex-column flex-fill m-5 patient-chart-container'>
      {TemplateCollection.get(productType)}
      {ActionCollection.get(actionType)}
    </div>
  );
};

export default Approve;
